import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFT_VARIANT_ID = 49741134397592;
const GIFT_PROPERTY = '_automatic_gift';
const GIFT_PROPERTY_VALUE = '$200+ Free Gift';
const THRESHOLD_CENTS = 20000;

let syncPromise = null;
let scheduledTimer = null;

function route(path) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}${path}`.replace(/\/\/{2,}/g, '/');
}

function isAutomaticGift(item) {
  return Number(item.variant_id) === GIFT_VARIANT_ID && item.properties?.[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE;
}

function getEligibleSubtotal(cart) {
  return cart.items.reduce((subtotal, item) => subtotal + (isAutomaticGift(item) ? 0 : item.final_line_price), 0);
}

function getSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : null)
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(route('cart.js'), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
  return response.json();
}

async function mutateCart(path, body) {
  const sectionIds = getSectionIds();
  const response = await fetch(route(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      ...body,
      ...(sectionIds.length ? { sections: sectionIds, sections_url: window.location.pathname } : {}),
    }),
  });
  const cart = await response.json();
  if (!response.ok || cart.status) throw new Error(cart.description || cart.message || `Cart update failed (${response.status}).`);
  return cart;
}

function announceCartUpdate(cart) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'update',
    context: 'cart',
    lines: [],
    promise: deferred.promise,
  }));
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: false,
      items: cart.items,
      sections: cart.sections,
      source: 'automatic-gift-200',
    },
  });
}

async function syncAutomaticGift() {
  if (syncPromise) return syncPromise;

  syncPromise = (async () => {
    const cart = await fetchCart();
    const giftLines = cart.items.filter(isAutomaticGift);
    const qualifies = getEligibleSubtotal(cart) >= THRESHOLD_CENTS;
    let updatedCart = null;

    if (qualifies && giftLines.length === 0) {
      updatedCart = await mutateCart('cart/add.js', {
        items: [{
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE },
        }],
      });
    } else if (!qualifies && giftLines.length > 0) {
      updatedCart = await mutateCart('cart/update.js', {
        updates: Object.fromEntries(giftLines.map((item) => [item.key, 0])),
      });
    } else if (qualifies && (giftLines.length > 1 || giftLines[0]?.quantity !== 1)) {
      updatedCart = await mutateCart('cart/update.js', {
        updates: Object.fromEntries(giftLines.map((item, index) => [item.key, index === 0 ? 1 : 0])),
      });
    }

    if (updatedCart) announceCartUpdate(updatedCart);
  })()
    .catch((error) => console.error('[automatic-gift-200]', error))
    .finally(() => {
      syncPromise = null;
    });

  return syncPromise;
}

function scheduleSync(delay = 80) {
  window.clearTimeout(scheduledTimer);
  scheduledTimer = window.setTimeout(syncAutomaticGift, delay);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.promise) {
    event.promise.then(() => scheduleSync()).catch(() => {});
  } else {
    scheduleSync();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSync(0), { once: true });
} else {
  scheduleSync(0);
}

window.addEventListener('pageshow', () => scheduleSync(0));
